// frontend/src/config/moduleRegistry.js
// Data-driven table of every dashboard module that can be opened as an MDI
// panel — mirrors the existing MODULE_ROLES convention in this directory's
// roleModules.js (a flat config array/object instead of a long if/else
// chain), but for a different concern: roleModules.js decides whether a
// module is VISIBLE in the nav for a given role/tenant; this file decides
// HOW to render it once opened (which component, what props it needs).
// Role/entitlement gating in Dashboard.js is unchanged by this file.
//
// `key` values are reused VERBATIM from the pre-MDI activeTab string ids
// (see the old getInitialTab's validTabs array) so the existing hash-based
// deep-link format (#room-mgmt, #room-mgmt-<timestamp> for the right-
// click-new-browser-tab feature) keeps working unchanged — no renaming
// migration needed anywhere else in the app.
//
// needsUser / supportsFocus / pollingAware encode real, verified prop
// variance across the 20 modules (see the project plan, "Verified facts"
// section) — Documents takes NO props at all; KDS/Accounting/TablesPOS
// take `focus` but not `user`; Recipes/Reports/Staff/UserManagement/
// Procurement/Budgets take neither. Only KDS and TablesPOS have live
// setInterval polling (confirmed: KDS.js:120,131, TablesPOS.js:97) and are
// flagged pollingAware so PanelHost knows to pass an `active` prop.

import {
  Smartphone,
  CalendarCheck,
  HeartPulse,
  DoorOpen,
  Presentation,
  QrCode,
  Armchair,
  CalendarDays,
  CircleDollarSign,
  Utensils,
  ShieldCheck,
  Droplets,
  Sparkles,
  ChefHat,
  Package,
  FileBarChart,
  Users,
  UserCog,
  Settings as SettingsIcon,
  Landmark,
  ShieldAlert,
  Truck,
  Target,
  Wallet,
  FileText,
  FolderArchive,
  MessagesSquare,
  Bell,
  Receipt,
  Building2,
  Contact,
  ScanLine,
  ClipboardList,
  Boxes,
  ClipboardCheck,
} from 'lucide-react';

import lazyWithRetry from '../lib/lazyWithRetry';

// Each screen is loaded the first time it is opened (see lib/lazyWithRetry.js).

const EnhancedBookingManagement = lazyWithRetry(() => import('../pages/dashboard/EnhancedBookingManagement'));
const RoomManagement = lazyWithRetry(() => import('../pages/dashboard/RoomManagement'));
const RoomStatus = lazyWithRetry(() => import('../pages/dashboard/RoomStatus'));
const ConferenceEvents = lazyWithRetry(() => import('../pages/dashboard/ConferenceEvents'));
const MealCards = lazyWithRetry(() => import('../pages/dashboard/MealCards'));
const AssetRegister = lazyWithRetry(() => import('../pages/dashboard/AssetRegister'));
const MealPlan = lazyWithRetry(() => import('../pages/dashboard/MealPlan'));
const POSSystem = lazyWithRetry(() => import('../pages/dashboard/POS'));
const TablesPOS = lazyWithRetry(() => import('../pages/dashboard/TablesPOS'));
const AdminPOS = lazyWithRetry(() => import('../pages/dashboard/AdminPOS'));
const WaterWing = lazyWithRetry(() => import('../pages/dashboard/WaterWing'));
const SpaGym = lazyWithRetry(() => import('../pages/dashboard/SpaGym'));
const Recipes = lazyWithRetry(() => import('../pages/dashboard/Recipes'));
const KDS = lazyWithRetry(() => import('../pages/dashboard/KDS'));
const Inventory = lazyWithRetry(() => import('../pages/dashboard/Inventory'));
const Reports = lazyWithRetry(() => import('../pages/dashboard/Reports'));
const Staff = lazyWithRetry(() => import('../pages/dashboard/Staff'));
const UserManagement = lazyWithRetry(() => import('../pages/dashboard/UserManagement'));
const RoleManagement = lazyWithRetry(() => import('../pages/dashboard/RoleManagement'));
const Settings = lazyWithRetry(() => import('../pages/dashboard/Settings'));
const Accounting = lazyWithRetry(() => import('../pages/dashboard/Accounting'));
const LedgerAdjustments = lazyWithRetry(() => import('../pages/dashboard/LedgerAdjustments'));
const LedgerHold = lazyWithRetry(() => import('../pages/dashboard/LedgerHold'));
const Procurement = lazyWithRetry(() => import('../pages/dashboard/Procurement'));
const Budgets = lazyWithRetry(() => import('../pages/dashboard/Budgets'));
const PettyCash = lazyWithRetry(() => import('../pages/dashboard/PettyCash'));
const Documents = lazyWithRetry(() => import('../pages/dashboard/Documents'));
const Chat = lazyWithRetry(() => import('../pages/dashboard/Chat'));
const Notifications = lazyWithRetry(() => import('../pages/dashboard/Notifications'));
const ShiftReport = lazyWithRetry(() => import('../pages/dashboard/ShiftReport'));
const Claims = lazyWithRetry(() => import('../pages/dashboard/Claims'));
const CorporateClients = lazyWithRetry(() => import('../pages/dashboard/CorporateClients'));
const CustomerCentre = lazyWithRetry(() => import('../pages/dashboard/CustomerCentre'));
const PurchaseReceipts = lazyWithRetry(() => import('../pages/dashboard/PurchaseReceipts'));
const TaxHealth = lazyWithRetry(() => import('../pages/dashboard/TaxHealth'));
const StockIssuesStandalone = lazyWithRetry(() => import('../pages/dashboard/StockIssuesTab').then((m) => ({ default: m.StockIssuesStandalone })));
const PortionStock = lazyWithRetry(() => import('../pages/dashboard/PortionStock'));
const KitchenSheet = lazyWithRetry(() => import('../pages/dashboard/KitchenSheet'));
const DailyManagementReport = lazyWithRetry(() => import('../pages/dashboard/DailyManagementReport'));
const GuestPortal = lazyWithRetry(() => import('../pages/dashboard/GuestPortal'));
const TalitHub = lazyWithRetry(() => import('../pages/dashboard/TalitHub'));
const TalitReportsHub = lazyWithRetry(() => import('../pages/dashboard/TalitHub').then((m) => ({ default: m.TalitReportsHub })));

export const MODULE_REGISTRY = [
  { key: 'bookings', label: 'Bookings', icon: CalendarCheck, component: EnhancedBookingManagement, needsUser: true, supportsFocus: true },
  { key: 'room-mgmt', label: 'Room Management', icon: DoorOpen, component: RoomManagement, needsUser: true, supportsFocus: true },
  // Read-only-plus-mark-clean room status view for housekeeping (2026-09-21
  // fix — see docs/KNOWN_GAPS.md). superuser/manager/supervisor can also see
  // it, but they'd normally use the fuller Room Management screen instead.
  { key: 'room-status', label: 'Room Status', icon: DoorOpen, component: RoomStatus, needsUser: true, pollingAware: true },
  { key: 'conference', label: 'Conference & Events', icon: Presentation, component: ConferenceEvents, needsUser: true },
  { key: 'meal-cards', label: 'Meal Cards', icon: QrCode, component: MealCards, needsUser: true },
  // Asset Register (migration 0147): beds, chairs, TVs, vehicles ... per room/area, with condition and a change trail.
  { key: 'asset-register', label: 'Asset Register', icon: Armchair, component: AssetRegister, needsUser: true },
  // Guest Portal (migration 0144): share guests their link, kitchen's breakfast pre-orders, service requests.
  { key: 'guest-portal', label: 'Guest Portal', icon: Smartphone, component: GuestPortal, needsUser: true },
  { key: 'pos', label: 'Point of Sale', icon: CircleDollarSign, component: POSSystem, needsUser: true },
  { key: 'tables', label: 'Tables', icon: Utensils, component: TablesPOS, needsUser: true, supportsFocus: true, pollingAware: true },
  { key: 'admin-pos', label: 'POS Admin', icon: ShieldCheck, component: AdminPOS, needsUser: true },
  { key: 'water-wing', label: 'Water Wing', icon: Droplets, component: WaterWing, needsUser: true, supportsFocus: true },
  { key: 'spa-gym', label: 'Spa & Gym', icon: Sparkles, component: SpaGym, needsUser: true, supportsFocus: true },
  { key: 'recipes', label: 'Recipes', icon: ChefHat, component: Recipes, needsUser: true },
  { key: 'kds', label: 'Kitchen Display', icon: ChefHat, component: KDS, needsUser: false, supportsFocus: true, pollingAware: true },
  { key: 'inventory', label: 'Inventory', icon: Package, component: Inventory, needsUser: true, supportsFocus: true },
  { key: 'reports', label: 'Reports', icon: FileBarChart, component: Reports, needsUser: false },
  { key: 'staff', label: 'Staff', icon: Users, component: Staff, needsUser: false },
  { key: 'users', label: 'User Management', icon: UserCog, component: UserManagement, needsUser: true },
  { key: 'roles', label: 'Role Management', icon: ShieldCheck, component: RoleManagement, needsUser: false },
  { key: 'settings', label: 'Settings', icon: SettingsIcon, component: Settings, needsUser: true },
  { key: 'accounting', label: 'Accounting', icon: Landmark, component: Accounting, needsUser: false, supportsFocus: true },
  { key: 'ledger-adjustments', label: 'Ledger Adjustments', icon: ShieldAlert, component: LedgerAdjustments, needsUser: false },
  { key: 'post-the-day', label: 'Post the Day', icon: ClipboardCheck, component: LedgerHold, needsUser: true },
  // needsUser flipped true 2026-09: Procurement.js now takes a `user` prop
  // to hide the 5 tabs chef can't reach (worker/lib/auth.js's
  // PROCUREMENT_REQUEST only grants Requisitions) instead of showing all
  // 6 tab buttons and letting the backend 403 the other 5 on click.
  { key: 'procurement', label: 'Procurement', icon: Truck, component: Procurement, needsUser: true },
  { key: 'budgets', label: 'Budgets', icon: Target, component: Budgets, needsUser: false },
  { key: 'petty-cash', label: 'Petty Cash', icon: Wallet, component: PettyCash, needsUser: true },
  { key: 'documents', label: 'Documents', icon: FileText, component: Documents, needsUser: false },
  { key: 'claims', label: 'Claims', icon: FolderArchive, component: Claims, needsUser: false },
  { key: 'chat', label: 'Chat', icon: MessagesSquare, component: Chat, needsUser: true },
  { key: 'notifications', label: 'Notifications', icon: Bell, component: Notifications, needsUser: false, isNotificationsPanel: true },
  { key: 'shift-report', label: 'Shift Report', icon: Receipt, component: ShiftReport, needsUser: false },
  { key: 'corporate-clients', label: 'Corporate Clients', icon: Building2, component: CorporateClients, needsUser: false },
  // needsFocusPanel — "Book a room" on a returning guest opens Bookings prefilled
  { key: 'customers', label: 'Customer Centre', icon: Contact, component: CustomerCentre, needsUser: false, needsFocusPanel: true },
  { key: 'purchase-receipts', label: 'Purchase Receipts', icon: ScanLine, component: PurchaseReceipts, needsUser: false },
  { key: 'tax-health', label: 'Tax Health', icon: HeartPulse, component: TaxHealth, needsUser: false },
  // Deliberately its own nav item, not only the tab already embedded
  // inside Inventory.js — STOCK_ISSUE_REQUEST (worker/lib/auth.js) is now
  // open to every role, most of whom have no other reason to open the
  // broader Inventory page. needsUser true — StockIssuesTab.js gates its
  // own "Issue"/"Cancel" buttons on user.role (canIssueStock), and
  // supportsFocus true — a stock-issue notification click deep-links here;
  // worker/lib/stockIssues.js's emitNotification call was updated to
  // linkTab: 'stock-issues' (previously 'inventory', when this only lived
  // as a tab there) to match.
  { key: 'stock-issues', label: 'Stock Issues', icon: ClipboardList, component: StockIssuesStandalone, needsUser: true, supportsFocus: true },
  { key: 'portion-stock', label: 'Portion Stock', icon: Boxes, component: PortionStock, needsUser: true },
  { key: 'kitchen-sheet', label: 'Kitchen Sheet', icon: ClipboardCheck, component: KitchenSheet, needsUser: true, needsFocusPanel: true },
  { key: 'meal-plan', label: 'Meal Plan', icon: CalendarDays, component: MealPlan, needsUser: false },
  // Daily Hotel Sales & Collection Report (2026-10-04) — the owner's own
  // "SAWAN management cockpit," a single-screen aggregation over every
  // department's own existing report (worker/lib/dailyManagementReport.js).
  // needsFocusPanel: true since it uses onOpenTab to deep-link into each
  // department's own detailed panel (Bookings/Kitchen Sheet/Spa & Gym/
  // Water Wing/Inventory), same convention as kitchen-sheet above.
  { key: 'daily-management-report', label: 'Daily Hotel Sales & Collection', icon: FileBarChart, component: DailyManagementReport, needsUser: false, needsFocusPanel: true },
  // TALIT, the AI assistant (2026-10-12): one screen with Ask TALIT (chat —
  // worker/lib/talitChat.js), the daily / weekly / monthly business reports
  // (worker/lib/talit.js) and recipe tuning. needsUser: the views shown depend
  // on the person's role.
  { key: 'talit', label: 'TALIT', icon: Sparkles, component: TalitHub, needsUser: true },
  // A TALIT report notification deep-links here: the same hub, opened on Reports.
  { key: 'talit-reports', label: 'TALIT Business Reports', icon: FileBarChart, component: TalitReportsHub, needsUser: true },
];

// Screens worth warming up in idle time right after sign-in, so the first
// click on the busiest ones is instant. The rest load on first open.
export const PRELOAD_KEYS = ['tables', 'pos', 'bookings', 'room-mgmt', 'kds', 'kitchen-sheet'];

export const MODULE_ICONS = Object.fromEntries(MODULE_REGISTRY.map((m) => [m.key, m.icon]));

export function getModuleEntry(key) {
  return MODULE_REGISTRY.find((m) => m.key === key) || null;
}
